import { useEffect, useState } from "react";
import {
	DialogContent,
	Typography,
	Box,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Stack,
	Chip,
	Divider,
	alpha,
	useTheme,
} from "@mui/material";
import { AssignmentReturn, LocalShippingOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { Z_INDEX } from "../../styles/zIndex";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUser,
	faCalendarAlt,
	faHashtag,
	faPerson,
	faPeopleCarryBox,
	faWarehouse,
	faFilePdf,
	faCalendarDay,
	faWallet,
	faMoneyBill,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Util } from "../../util/Util";
import { useAppSocket } from "../../providers/AppSocketContext";
import { isSupplyOverdue, isSupplyReturn } from "./supplyHelpers";

import { addMoney, roundMoney } from "../../util/quantity";
import { canExportPdf } from "../../util/access";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

const formatSigned = (value: number) => {
	const num = Number(value);
	if (!Number.isFinite(num)) return Util.formatNumber(0);
	if (num < 0) return `(${Util.formatNumber(Math.abs(num))})`;
	return Util.formatNumber(num);
};

const SupplyReport = ({ isOpen, toggle, bundle }: Props) => {
	const [record, setRecord] = useState<any>(null);
	const theme = useTheme();

	const { api, access } = useAuth();
	const { queueReRun, setLoadingMessage, t } = useUI();
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor(appSettings?.UI_THEME_PRIMARY?.value);

	const isReturn = Boolean(
		record?.details?.is_return
		|| isSupplyReturn({ batch: bundle?.batch, sup_type: record?.details?.sup_type }),
	);
	const accentColor = isReturn ? theme.palette.warning.main : themeColor;

	const fetchData = async () => {
		const res = await api.get(`supplies/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				setLoadingMessage(null);
				return;
			}
			if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
				return;
			}
			if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		toggle();
	};

	const handlePrint = async () => {
		if (!canExportPdf(access, "supply")) return;
		setLoadingMessage("EXPORT");

		const res = await api.get(`reports/supply/${bundle.batch}`);

		if (res?.status === 200) {
			const pdfData = atob(res.data.data);
			const blob = new Blob([Uint8Array.from(pdfData, (c) => c.charCodeAt(0))], { type: "application/pdf" });
			window.open(URL.createObjectURL(blob));
		} else {
			console.error("Failed to fetch data:", res);
		}

		setLoadingMessage(null);
	};

	useEffect(() => {
		if (isOpen && bundle?.batch) {
			setRecord(null);
			fetchData();
		}
	}, [isOpen, bundle?.batch]);

	const subtotal = record?.records?.reduce((acc: number, r: any) => addMoney(acc, (r?.total || 0)), 0) || 0;
	const grandTotal = subtotal;
	const amountPaid = record?.payments?.reduce((acc: number, p: any) => addMoney(acc, (p.amount || 0)), 0) || 0;
	const balanceDue = roundMoney(grandTotal - amountPaid);

	const isOverdue = isSupplyOverdue({
		balance: balanceDue,
		due_date: record?.details?.due_date,
	});

	const paymentStatus = balanceDue === 0
		? t("common:status.fullyPaid")
		: isOverdue
			? t("common:status.overdue")
			: balanceDue === grandTotal
				? t("common:status.unpaid")
				: t("common:status.partialPayment");

	const statusColor = balanceDue === 0
		? "success"
		: isOverdue || balanceDue === grandTotal
			? "error"
			: "warning";

	return (
		<MoleDialog
			open={isOpen}
			onClose={toggle}
			maxWidth="md"
			fullWidth
			themeColor={accentColor}
			paperOptions={{ maxHeight: "95vh" }}
			sx={{ zIndex: Z_INDEX.nestedDialog }}
		>
			<MoleDialogHeader
				eyebrow={isReturn ? t("common:report.returnSupply") : t("common:supplyReport.title")}
				title={record?.details?.reference || bundle?.reference || t("common:status.loading")}
				subtitle={
					<Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
						<Typography variant="body2" color="text.secondary">{t("common:ui.labels.status")}</Typography>
						<Chip
							label={paymentStatus}
							size="small"
							color={statusColor}
							sx={{ fontWeight: 700 }}
						/>
						{isReturn && (
							<Chip
								icon={<AssignmentReturn sx={{ fontSize: "16px !important" }} />}
								label={`${t("common:tables.type")}: ${t("common:ui.buttons.return")}`}
								size="small"
								sx={{
									fontWeight: 700,
									bgcolor: alpha(theme.palette.warning.main, 0.12),
									color: "warning.dark",
								}}
							/>
						)}
					</Stack>
				}
				icon={isReturn ? <AssignmentReturn /> : <LocalShippingOutlined />}
				onClose={toggle}
				themeColor={accentColor}
			/>

			<DialogContent sx={{ p: 4 }}>
				{record ? (
					<Box>
						<Grid container spacing={3} sx={{ mb: 4 }}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography variant="overline" sx={{ color: "text.secondary", fontWeight: 800 }}>
									{t("common:report.supplyDetails")}
								</Typography>
								<Paper
									variant="outlined"
									sx={{
										p: 2,
										mt: 1,
										bgcolor: isReturn
											? alpha(theme.palette.warning.main, 0.04)
											: "background.default",
										borderStyle: "dashed",
										borderColor: isReturn
											? alpha(theme.palette.warning.main, 0.35)
											: "divider",
									}}
								>
									<Stack spacing={1.5}>
										<DetailItem icon={faHashtag} label={t("common:supplyReport.reference")} value={record?.details.reference} />
										<DetailItem icon={faCalendarAlt} label={t("common:supplyReport.date")} value={Util.formatDate(record?.details.date, "full")} />
										{record?.details?.due_date && (
											<DetailItem
												icon={faCalendarDay}
												label={t("common:supplyReport.dueDate")}
												value={Util.formatDate(record.details.due_date, "full")}
											/>
										)}
										<DetailItem icon={faPeopleCarryBox} label={t("common:supplyReport.suppliedBy")} value={record?.details.supplier} />
										<DetailItem icon={faWarehouse} label={t("common:supplyReport.suppliedDepot")} value={record?.details.depot} />
										<DetailItem icon={faPerson} label={t("common:tables.observer")} value={record?.details.client} />
										<DetailItem icon={faUser} label={t("common:supplyReport.recordedBy")} value={record?.details.user} />
									</Stack>
								</Paper>
							</Grid>
						</Grid>

						<TableContainer
							component={Paper}
							elevation={0}
							sx={{
								border: "1px solid",
								borderColor: isReturn
									? alpha(theme.palette.warning.main, 0.35)
									: "divider",
								borderRadius: 2,
								mb: 4,
							}}
						>
							<Table size="small">
								<TableHead sx={{ bgcolor: isReturn ? alpha(theme.palette.warning.main, 0.08) : "action.hover" }}>
									<TableRow>
										<TableCell sx={{ fontWeight: 700, py: 2 }}>{t("common:supplyReport.description")}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:supplyReport.quantity")}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:supplyReport.unitPrice")}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:supplyReport.amount")}</TableCell>
										<TableCell align="right" sx={{ fontWeight: 700 }}>
											<FontAwesomeIcon icon={faCalendarDay} size="sm" /> {t("common:supplyReport.expiry")}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{record.records?.map((item: any, idx: number) => (
										<TableRow
											key={idx}
											hover
											sx={isReturn ? { bgcolor: alpha(theme.palette.warning.main, 0.03) } : undefined}
										>
											<TableCell sx={{ py: 1.5 }}>
												<Typography variant="body2" fontWeight={600}>{item.item_name}</Typography>
												<Typography variant="caption" color="text.secondary">
													{t("common:report.code", { code: item.item_code || t("common:ui.placeholders.notAvailable") })}
												</Typography>
											</TableCell>
											<TableCell
												align="right"
												sx={{ color: Number(item.quantity) < 0 ? "error.main" : undefined, fontWeight: 600 }}
											>
												{formatSigned(item.quantity)}
											</TableCell>
											<TableCell align="right">{Util.formatNumber(item.amount)}</TableCell>
											<TableCell
												align="right"
												sx={{ fontWeight: 700, color: Number(item.total) < 0 ? "error.main" : undefined }}
											>
												{formatSigned(item.total)}
											</TableCell>
											<TableCell align="right">{item.expiry ? Util.formatDate(item.expiry, "medium") : "—"}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						<Grid container spacing={4}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
									<FontAwesomeIcon icon={faWallet} style={{ color: accentColor }} />
									{isReturn
										? t("common:report.refundBreakdown")
										: t("common:report.paymentBreakdown")}
								</Typography>
								<Stack spacing={1}>
									{record.payments?.map((payment: any, idx: number) => (
										<Box
											key={idx}
											sx={{
												display: "flex",
												justifyContent: "space-between",
												alignItems: "center",
												p: 1.5,
												borderRadius: 2,
												bgcolor: "action.hover",
												border: "1px solid",
												borderColor: "divider",
											}}
										>
											<Stack direction="row" spacing={1.5} alignItems="center">
												<Box sx={{ color: "text.secondary" }}>
													<FontAwesomeIcon
														icon={payment.mode?.toLowerCase() === "cash" ? faMoneyBill : faWallet}
														size="sm"
													/>
												</Box>
												<Typography variant="body2" fontWeight={600}>{payment.mode}</Typography>
											</Stack>
											<Typography variant="body2" fontWeight={700}>
												{Util.formatNumber(payment.amount)}
											</Typography>
										</Box>
									))}
									{(!record.payments || record.payments.length === 0) && (
										<Typography variant="body2" color="text.secondary" fontStyle="italic">
											{isReturn
												? t("common:report.noRefundRecords")
												: t("common:report.noPaymentRecords")}
										</Typography>
									)}
								</Stack>

								{record.details?.description && (
									<Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "action.hover", borderLeft: "4px solid", borderColor: isReturn ? "warning.main" : "info.main" }}>
										<Typography variant="caption" fontWeight={800} color={isReturn ? "warning.main" : "info.main"} sx={{ textTransform: "uppercase" }}>
											{t("common:taskPane.notes")}
										</Typography>
										<Typography variant="body2" sx={{ mt: 0.5 }}>{record.details.description}</Typography>
									</Box>
								)}
							</Grid>

							<Grid size={{ xs: 12, sm: 6 }}>
								<Paper
									variant="outlined"
									sx={{
										p: 3,
										borderRadius: 3,
										bgcolor: isReturn ? "grey.900" : (appSettings?.UI_THEME_PRIMARY?.value || "primary.main"),
										color: "white",
										borderColor: isReturn ? alpha(theme.palette.warning.main, 0.5) : undefined,
									}}
								>
									<Stack spacing={1.5}>
										<SummaryRow label={t("common:report.subtotal")} value={formatSigned(subtotal)} />
										<Divider sx={{ borderColor: "rgba(255,255,255,0.1)", my: 1 }} />
										<Stack direction="row" justifyContent="space-between" alignItems="center">
											<Typography variant="h6" fontWeight={400}>{t("common:report.grandTotal")}</Typography>
											<Typography
												variant="h5"
												fontWeight={900}
												sx={{ color: isReturn ? "warning.main" : accentColor }}
											>
												{formatSigned(grandTotal)}
											</Typography>
										</Stack>
										<SummaryRow
											label={isReturn ? t("common:report.totalRefunded") : t("common:report.totalPaid")}
											value={Util.formatNumber(amountPaid)}
											opacity={0.7}
										/>
										{balanceDue !== 0 && (
											<Box
												sx={{
													mt: 1,
													p: 1,
													bgcolor: "rgba(255, 152, 0, 0.1)",
													borderRadius: 1,
													display: "flex",
													justifyContent: "space-between",
													color: "warning.main",
												}}
											>
												<Typography variant="body2" fontWeight={700}>{t("common:report.balanceDue")}</Typography>
												<Typography variant="body2" fontWeight={800}>{formatSigned(balanceDue)}</Typography>
											</Box>
										)}
									</Stack>
								</Paper>
							</Grid>
						</Grid>
					</Box>
				) : (
					<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
						<CircularProgress thickness={5} size={48} sx={{ color: accentColor }} />
						<Typography sx={{ mt: 2, color: "text.secondary" }}>
							{t("common:supplyReport.loading")}
						</Typography>
					</Stack>
				)}
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				{canExportPdf(access, "supply") && (
					<MolePrimaryButton
						startIcon={<FontAwesomeIcon icon={faFilePdf} />}
						onClick={handlePrint}
						disabled={!record}
					>
						{t("common:ui.buttons.exportAsPdf")}
					</MolePrimaryButton>
				)}
			</MoleDialogActions>
		</MoleDialog>
	);
};

const SummaryRow = ({ label, value, opacity = 1 }: { label: string; value: string; opacity?: number }) => (
	<Stack direction="row" justifyContent="space-between" sx={{ opacity }}>
		<Typography variant="body2">{label}</Typography>
		<Typography variant="body2" fontWeight={600}>{value}</Typography>
	</Stack>
);

const DetailItem = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b" }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>{label}:</Typography>
		<Typography variant="body2" fontWeight={600}>{value || "—"}</Typography>
	</Stack>
);

export default SupplyReport;